import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import firebaseFunctionsService from '../../services/firebaseFunctionsService';
import {
  MessageCircle,
  CalendarDays,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  User,
  DollarSign,
  Star,
  MessageSquare,
} from 'lucide-react';
import airportImage from '../../assets/airportt.png';
import ReviewModal from '../../components/reviews/ReviewModal';
import { hasExistingReview } from '../../services/reviewsService';
import { useAppDispatch } from '../../store/hooks';
import { openOrCreateConversationByUserId } from '../../store/chatThunks';

const PAGE_SIZE = 12;

const getStatusIcon = status => {
  switch (status) {
    case 'pending':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'accepted':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'rejected':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'cancelled':
      return <XCircle className="h-4 w-4 text-gray-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusColor = status => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300';
    case 'accepted':
      return 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300';
    case 'rejected':
      return 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300';
    case 'completed':
      return 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300';
    case 'cancelled':
      return 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-900/20 dark:border-gray-800 dark:text-gray-300';
    default:
      return 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-gray-900/20 dark:border-gray-800 dark:text-gray-300';
  }
};

const getStatusLabel = (status, t) => {
  switch (status) {
    case 'pending':
      return t('flexyRideApplications.statusPending');
    case 'accepted':
      return t('flexyRideApplications.statusAccepted');
    case 'rejected':
      return t('flexyRideApplications.statusRejected');
    case 'completed':
      return t('flexyRideApplications.statusCompleted');
    case 'cancelled':
      return t('flexyRideApplications.statusCancelled');
    default:
      return status?.toUpperCase() || t('flexyRideApplications.statusUnknown');
  }
};

export default function FlexyRideMyRequests() {
  const navigate = useNavigate();
  const { user, initializing } = usePublicAuth();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [applications, setApplications] = useState([]);
  const [listings, setListings] = useState({});
  const [ownerInfo, setOwnerInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [reviewModal, setReviewModal] = useState({
    visible: false,
    reviewedUserId: null,
    reviewedUserName: '',
    requestId: null,
    role: 'driver',
  });
  const [reviewStatuses, setReviewStatuses] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    // Show applications user submitted (applicantId == currentUser.uid)
    // Only seat applications
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'ride_requests'),
        where('applicantId', '==', user.uid),
      ),
      async snapshot => {
        const requests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch listing details for each request
        const applicationsWithListings = await Promise.all(
          requests.map(async request => {
            try {
              const listingDoc = await getDoc(
                doc(db, 'ride_listings', request.listingId),
              );

              return {
                ...request,
                listing: listingDoc.exists()
                  ? { id: listingDoc.id, ...listingDoc.data() }
                  : null,
              };
            } catch (error) {
              console.error('Error fetching listing:', error);
              return { ...request, listing: null };
            }
          }),
        );

        // Only seat applications
        const filteredApplications = applicationsWithListings.filter(
          app => app.listing?.type === 'seat',
        );

        // Sort by createdAt descending
        filteredApplications.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || a.createdAt || 0;
          const bTime = b.createdAt?.toMillis?.() || b.createdAt || 0;
          return bTime - aTime;
        });

        setApplications(filteredApplications);

        // Store listings
        const listingsMap = {};
        filteredApplications.forEach(app => {
          if (app.listing) {
            listingsMap[app.listingId] = app.listing;
          }
        });
        setListings(listingsMap);

        setLoading(false);
      },
      error => {
        console.error('Error fetching applications:', error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Fetch owner info for all applications
  useEffect(() => {
    const fetchOwners = async () => {
      const ownerIds = Array.from(
        new Set(
          applications
            .map(app => app.listingOwnerId || app.listing?.createdBy)
            .filter(Boolean),
        ),
      );

      const newOwnerInfo = { ...ownerInfo };
      for (const ownerId of ownerIds) {
        if (newOwnerInfo[ownerId]) continue;
        try {
          const ownerDoc = await getDoc(doc(db, 'users', ownerId));
          if (ownerDoc.exists()) {
            const data = ownerDoc.data();
            newOwnerInfo[ownerId] =
              data.displayName ||
              data.username ||
              data.email?.split('@')[0] ||
              'Flexy user';
          }
        } catch (error) {
          console.error('Error fetching owner info:', error);
        }
      }
      setOwnerInfo(newOwnerInfo);
    };

    if (applications.length > 0) {
      fetchOwners();
    }
  }, [applications]);

  // Fetch review statuses
  useEffect(() => {
    if (!user?.uid || applications.length === 0) {
      setReviewStatuses({});
      return;
    }

    let isMounted = true;

    const fetchStatuses = async () => {
      try {
        const entries = await Promise.all(
          applications.map(async application => {
            const reviewedUserId =
              application.listingOwnerId || application.listing?.createdBy;
            if (!reviewedUserId) {
              return [application.id, false];
            }
            try {
              const exists = await hasExistingReview(
                reviewedUserId,
                application.id,
                user.uid,
              );
              return [application.id, exists];
            } catch (error) {
              console.error('Error checking review status:', error);
              return [application.id, false];
            }
          }),
        );

        if (!isMounted) return;
        const statusMap = {};
        entries.forEach(([id, status]) => {
          if (id) {
            statusMap[id] = status;
          }
        });
        setReviewStatuses(statusMap);
      } catch (error) {
        console.error('Error loading review statuses:', error);
      }
    };

    fetchStatuses();

    return () => {
      isMounted = false;
    };
  }, [applications, user?.uid]);

  const handleOpenPaymentModal = application => {
    const rawPrice = application?.price || application?.listing?.price || 0;
    const currency =
      application?.currency || application?.listing?.currency || 'CAD';
    const costNum =
      typeof rawPrice === 'string'
        ? parseFloat(String(rawPrice).replace(/[^0-9.]/g, ''))
        : Number(rawPrice);
    const fee = !isNaN(costNum) ? costNum * 0.15 : 0;
    const total = !isNaN(costNum) ? costNum + fee : 0;
    setPaymentModalData({
      requestId: application.id,
      currency,
      subtotal: costNum,
      fee,
      total,
    });
    setTipAmount('');
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    if (!paymentModalData || !user?.uid) return;

    if (confirmingPayment) return;

    try {
      setConfirmingPayment(true);

      const application = applications.find(
        app => app.id === paymentModalData.requestId,
      );
      if (!application) {
        throw new Error('Application not found');
      }

      const payerId = user.uid; // applicant (current user)
      const receiverId = application.listingOwnerId; // listing owner

      if (!payerId || !receiverId) {
        throw new Error('Payer or receiver ID missing');
      }

      const requestDoc = await getDoc(
        doc(db, 'ride_requests', paymentModalData.requestId),
      );

      if (!requestDoc.exists()) {
        throw new Error('Request not found');
      }

      const requestData = requestDoc.data();
      if (requestData.status === 'completed') {
        setShowPaymentModal(false);
        setPaymentModalData(null);
        alert('Payment already confirmed.');
        return;
      }

      const amount = Number(paymentModalData.subtotal || 0) || 0;
      const fee = Number(paymentModalData.fee || 0) || 0;
      const total = Number(paymentModalData.total || 0) || 0;
      const currency = paymentModalData.currency || 'CAD';
      const itemTitle =
        application.listing?.from && application.listing?.to
          ? `Ride: ${application.listing.from} → ${application.listing.to}`
          : 'Ride request';

      try {
        const authToken = await auth.currentUser?.getIdToken();
        if (!authToken) {
          throw new Error('User not authenticated');
        }

        const tipAmountValue = parseFloat(tipAmount || 0);
        const paymentResult =
          await firebaseFunctionsService.confirmDeliveryPayment(
            paymentModalData.requestId,
            payerId,
            receiverId,
            amount,
            fee,
            total,
            currency,
            itemTitle,
            authToken,
            tipAmountValue,
            'ride_requests',
          );

        if (paymentResult.alreadyCompleted) {
          setShowPaymentModal(false);
          setPaymentModalData(null);
          alert('Payment already confirmed.');
          return;
        }

        // Wait a moment for Firestore batch commit to complete
        await new Promise(resolve => setTimeout(resolve, 2000));

        // Mark request as completed in Firestore
        await updateDoc(doc(db, 'ride_requests', paymentModalData.requestId), {
          status: 'completed',
          completedAt: serverTimestamp(),
          confirmedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // Mark as completed locally
        setApplications(prev =>
          prev.map(app =>
            app.id === paymentModalData.requestId
              ? { ...app, status: 'completed' }
              : app,
          ),
        );

        setShowPaymentModal(false);
        setPaymentModalData(null);
        alert('Payment confirmed. Ride completed successfully!');
      } catch (paymentError) {
        console.error('Error processing payment:', paymentError);
        throw new Error(`Payment failed: ${paymentError.message}`);
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      alert(error.message || t('flexyRideMyRequests.paymentConfirmError'));
    } finally {
      setTipAmount('');
      setPaymentModalData(null);
      setConfirmingPayment(false);
    }
  };

  const handleOpenReviewModal = async application => {
    if (!user?.uid) {
      alert(t('flexyRideApplications.loginToRate'));
      return;
    }
    const reviewedUserId =
      application.listingOwnerId || application.listing?.createdBy;
    if (!reviewedUserId) {
      alert(t('flexyRideApplications.driverInfoMissing'));
      return;
    }
    try {
      const alreadyReviewed = await hasExistingReview(
        reviewedUserId,
        application.id,
        user.uid,
      );
      if (alreadyReviewed) {
        setReviewStatuses(prev => ({ ...prev, [application.id]: true }));
        alert(t('flexyRideApplications.alreadyReviewed'));
        return;
      }
      const reviewedUserName =
        ownerInfo[reviewedUserId] ||
        application.listingOwnerName ||
        t('flexyRideApplications.driverFallback');
      setReviewModal({
        visible: true,
        reviewedUserId,
        reviewedUserName,
        requestId: application.id,
        role: 'driver',
      });
    } catch (error) {
      alert(error.message || t('flexyRideApplications.reviewModalError'));
    }
  };

  const handleStartChat = async (targetUserId, targetUserName) => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    if (!targetUserId) {
      navigate('/chat');
      return;
    }

    try {
      const conversation = await dispatch(
        openOrCreateConversationByUserId(
          targetUserId,
          targetUserName || t('common.unknownUser'),
        ),
      );
      if (conversation?.id) {
        navigate(`/chat/${conversation.id}`);
      } else {
        navigate('/chat');
      }
    } catch (error) {
      console.error('Error opening chat:', error);
      navigate('/chat');
    }
  };

  // Pagination effect
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(applications.length / PAGE_SIZE));
    if (currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [applications.length, currentPage]);

  const renderApplicationCard = application => {
    const listing = application.listing;
    if (!listing) return null;

    const ownerId = application.listingOwnerId || listing.createdBy;
    const ownerName =
      ownerInfo[ownerId] ||
      application.listingOwnerName ||
      t('tripCard.flexyUser');

    return (
      <div className="group relative rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800 hover:shadow-xl hover:border-gray-300 dark:hover:border-slate-700 transition-all duration-300">
        {/* Header */}
        <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100 dark:border-slate-700">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-black dark:bg-slate-700 flex items-center justify-center">
                <User className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 dark:text-white truncate">
                  {ownerName}
                </h3>
                <p className="text-xs text-gray-600 dark:text-slate-400 truncate">
                  {t('flexyRideMyRequests.driverLabel')}
                </p>
              </div>
            </div>
          </div>
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${getStatusColor(
              application.status,
            )} flex-shrink-0`}
          >
            {getStatusIcon(application.status)}
            <span className="text-xs font-bold">
              {getStatusLabel(application.status, t)}
            </span>
          </div>
        </div>

        {/* Route Info */}
        <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
              {t('flexyRideMyRequests.route')}
            </p>
          </div>
          <p className="text-sm font-bold text-gray-900 dark:text-white">
            {listing.from || t('home.fromFallback')} →{' '}
            {listing.to || t('home.toFallback')}
          </p>
        </div>

        {/* Trip Details */}
        <div className="mb-4 space-y-2">
          {/* Date & time */}
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-blue-500 dark:text-blue-400" />
            <p className="text-xs text-gray-600 dark:text-slate-400">
              {listing.date || t('flexyRideSeat.dateNotSet')} •{' '}
              {listing.time || t('flexyRideSeat.timeNotSet')}
            </p>
          </div>

          {/* Price + seats info similar to mobile */}
          {(application.price || listing.price) && (
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-yellow-500 dark:text-yellow-400" />
              <p className="text-xs font-bold text-gray-900 dark:text-white">
                {t('flexyRideApplications.priceLabel')}{' '}
                {application.price
                  ? `${application.currency || 'CAD'} ${application.price}`
                  : listing.price
                  ? `${listing.currency || 'CAD'} $${listing.price}`
                  : t('flexyRideApplications.priceNA')}{' '}
                {listing.seats
                  ? t('flexyRideMyRequests.priceSeatsSuffix', {
                      count: listing.seats,
                    })
                  : null}
              </p>
            </div>
          )}

          {/* Vehicle details */}
          {(listing.carBrand || listing.carModel || listing.carYear) && (
            <div className="text-xs text-gray-600 dark:text-slate-300">
              <span className="font-semibold text-gray-800 dark:text-white">
                {t('flexyRideMyRequests.vehicleLabel')}{' '}
              </span>
              <span>
                {listing.carBrand}
                {listing.carModel ? ` ${listing.carModel}` : ''}
                {listing.carYear ? ` (${listing.carYear})` : ''}
              </span>
            </div>
          )}
        </div>

        {/* Status Messages */}
        {application.status === 'pending' && (
          <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
            <p className="text-xs font-semibold text-yellow-900 dark:text-yellow-200">
              {t('flexyRideApplications.offerSent')}
            </p>
          </div>
        )}

        {application.status === 'accepted' && (
          <>
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-xs font-semibold text-green-900 dark:text-green-200">
                {t('flexyRideMyRequests.acceptedMessage')}
              </p>
            </div>
          </>
        )}

        {application.status === 'completed' && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-xs font-semibold text-green-900 dark:text-green-200">
              {t('flexyRideMyRequests.completedMessage')}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-100 dark:border-slate-700 flex-wrap">
          {application.status === 'accepted' && (
            <button
              onClick={() => handleOpenPaymentModal(application)}
              disabled={confirmingPayment === application.id}
              className="flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckCircle className="h-5 w-5" />
              {t('flexyRideMyRequests.confirmPaymentButton')}
            </button>
          )}

          {application.status === 'completed' && (
            <button
              onClick={() => {
                if (!reviewStatuses[application.id]) {
                  handleOpenReviewModal(application);
                }
              }}
              disabled={reviewStatuses[application.id]}
              className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 shadow-sm ${
                reviewStatuses[application.id]
                  ? 'bg-gray-200 dark:bg-slate-800 text-gray-600 dark:text-slate-400 cursor-not-allowed'
                  : 'bg-yellow-500 text-black hover:bg-yellow-400'
              }`}
            >
              <Star className="h-4 w-4" />
              {reviewStatuses[application.id]
                ? t('flexyRideApplications.reviewed')
                : t('flexyRideApplications.rate')}
            </button>
          )}

          <button
            onClick={() => {
              handleStartChat(ownerId, ownerName);
            }}
            className="flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
          >
            <MessageCircle className="h-5 w-5" />
            {t('myRequests.message')}
          </button>
          <button
            onClick={() => navigate('/profile/support')}
            className="flex-1 min-w-[120px] flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-lg text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
          >
            <MessageSquare className="h-5 w-5" />
            {t('profile.supportTitle') || 'Support'}
          </button>
        </div>
      </div>
    );
  };

  const isLoading = initializing || loading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="h-16 bg-white/20 dark:bg-slate-700/50 rounded-xl mb-4 w-3/4 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-full animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>
        <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800"
              >
                <div className="h-6 bg-gray-200 dark:bg-slate-800 rounded-lg w-3/4 mb-4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-full mb-2 animate-pulse"></div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-6xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              {t('flexyRideMyRequests.loginToViewRequests')}
            </p>
            <a
              href="/auth/login"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-5 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('common.goToLogin')}
            </a>
          </div>
        </main>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(applications.length / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const paginatedApplications = applications.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[500px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('flexyRideMyRequests.title')}
              </h1>
              <p className="text-xl text-yellow-50 mb-8 max-w-2xl">
                {t('flexyRideMyRequests.subtitle')}
              </p>
            </div>
            <div className="relative">
              <img
                src={airportImage}
                alt="Airport"
                className="w-full h-auto object-contain"
                style={{ maxHeight: '400px' }}
              />
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
        {applications.length === 0 ? (
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-12 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <User className="h-16 w-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('flexyRideMyRequests.emptyTitle')}
            </h2>
            <p className="text-gray-600 dark:text-slate-400 mb-6">
              {t('flexyRideMyRequests.emptyDescription')}
            </p>
            <a
              href="/flexy-ride/driver"
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-6 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('flexyRideMyRequests.browseSeats')}
            </a>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedApplications.map(application => (
                <div key={application.id}>
                  {renderApplicationCard(application)}
                </div>
              ))}
            </div>
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('common.prev')}
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                      currentPage === index + 1
                        ? 'bg-black text-yellow-400 dark:bg-yellow-500 dark:text-black'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setCurrentPage(prev => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 transition hover:border-gray-500 disabled:opacity-50 dark:border-slate-700 dark:text-slate-100 dark:hover:border-slate-500"
                >
                  {t('common.next')}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Payment Modal */}
      {showPaymentModal && paymentModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 dark:border-slate-800">
            <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-4 text-center">
              {t('flexyRideMyRequests.modalTitle')}
            </h3>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-700">
                <span className="text-gray-600 dark:text-slate-400">
                  {t('flexyRideMyRequests.modalSubtotal')}
                </span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {paymentModalData.currency}{' '}
                  {paymentModalData.subtotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-700">
                <span className="text-gray-600 dark:text-slate-400">
                  {t('flexyRideMyRequests.modalFees')}
                </span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {paymentModalData.currency} {paymentModalData.fee.toFixed(2)}
                </span>
              </div>
              {/* Tip Input */}
              <div className="mt-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                  {t('flexyRideMyRequests.modalTipLabel')}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={tipAmount}
                  onChange={e => {
                    const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                    const parts = cleaned.split('.');
                    const filtered =
                      parts.length > 2
                        ? parts[0] + '.' + parts.slice(1).join('')
                        : cleaned;
                    setTipAmount(filtered);
                  }}
                  placeholder="0.00"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                />
              </div>
              {tipAmount && parseFloat(tipAmount) > 0 && (
                <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-700">
                  <span className="text-gray-600 dark:text-slate-400">
                    {t('flexyRideMyRequests.modalTip')}
                  </span>
                  <span className="font-bold text-gray-900 dark:text-white">
                    {paymentModalData.currency}{' '}
                    {parseFloat(tipAmount || 0).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center py-2">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {t('flexyRideMyRequests.modalTotal')}
                </span>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {paymentModalData.currency}{' '}
                  {(
                    paymentModalData.total + parseFloat(tipAmount || 0)
                  ).toFixed(2)}
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setTipAmount('');
                  setPaymentModalData(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmPayment}
                disabled={confirmingPayment}
                className="flex-1 px-4 py-3 bg-black dark:bg-yellow-500 text-yellow-500 dark:text-black rounded-xl font-bold hover:bg-gray-900 dark:hover:bg-yellow-400 transition disabled:opacity-50"
              >
                {confirmingPayment
                  ? t('flexyRideMyRequests.modalProcessing')
                  : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ReviewModal
        visible={reviewModal.visible}
        reviewedUserId={reviewModal.reviewedUserId}
        reviewedUserName={reviewModal.reviewedUserName}
        requestId={reviewModal.requestId}
        userRole={reviewModal.role}
        onClose={() =>
          setReviewModal(prev => ({
            ...prev,
            visible: false,
          }))
        }
        onSubmitSuccess={() => {
          if (reviewModal.requestId) {
            setReviewStatuses(prev => ({
              ...prev,
              [reviewModal.requestId]: true,
            }));
          }
        }}
      />
    </div>
  );
}
